// src/components/pipelines/CreatePipelineForm.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  ModalCloseButton, Button, VStack, FormControl, FormLabel, Input,
  Textarea, useColorModeValue, useToast, FormHelperText, FormErrorMessage,
  SimpleGrid, InputGroup, InputLeftElement, Icon, HStack, Text, Badge, Flex,
  Select, Alert, AlertIcon, Box, Divider, Tooltip,
} from '@chakra-ui/react';
import {
  FaRoute, FaInfoCircle, FaRulerCombined, FaCalendarAlt, FaMapMarkedAlt,
  FaTools, FaArrowDown, FaShieldAlt, FaClipboardCheck,
} from 'react-icons/fa';

import * as PipelineService from '../../services/pipelineService';
import {
  PIPELINE_STATUSES,
  DEPTH_SOURCES,
  validatePipelinePath,
  validatePipelineDepth,
} from '../../services/pipelineService';

// --- UPDATE: initial state hoisted out of the component --------------------
// It was rebuilt on every render, which makes it a new object identity each
// time and a hazard the moment it lands in a dependency array.
const INITIAL_FORM_STATE = {
  name: '',
  description: '',
  material: '',
  diameter_inches: '',
  install_date: '',
  path_coordinates_text: '',
  area_code: '',
  // --- clearance-relevant fields ---
  depth_m: '',
  depth_source: '',
  status: 'active',
  pressure_zone: '',
};

function CreatePipelineForm({ isOpen, onClose, onSuccess, initialCoordinates = null }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coordError, setCoordError] = useState('');
  const [depthError, setDepthError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const toast = useToast();

  // --- UPDATE: all useColorModeValue calls hoisted to the top level ---------
  // Two of these were previously called inside JSX props (_readOnly and the
  // ModalFooter bg). They happen to run in a stable order today, but a hook
  // inside a conditionally-rendered branch is a Rules-of-Hooks violation
  // waiting to happen.
  const modalBg = useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.800');
  const readOnlyBg = useColorModeValue('gray.50', 'gray.900');
  const footerBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const noteBg = useColorModeValue('blue.50', 'blue.900');
  const noteBorder = useColorModeValue('blue.200', 'blue.700');
  const helperColor = useColorModeValue('gray.600', 'gray.400');

  // Populate form: Reset or use initialCoordinates when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(initialCoordinates) && initialCoordinates.length >= 2) {
      try {
        setFormData({ ...INITIAL_FORM_STATE, path_coordinates_text: JSON.stringify(initialCoordinates, null, 2) });
      } catch (e) {
        console.error('Error stringifying initial coordinates:', e);
        setFormData(INITIAL_FORM_STATE);
      }
    } else {
      setFormData(INITIAL_FORM_STATE);
    }
    setCoordError('');
    setDepthError('');
    setWarnings([]);
  }, [isOpen, initialCoordinates]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'path_coordinates_text') setCoordError('');
    if (name === 'depth_m') setDepthError('');
  }, []);

  // --- UPDATE: live depth feedback -----------------------------------------
  // The consequence of leaving depth blank is invisible unless you say it out
  // loud, so the form says it while the user is deciding.
  const depthFeedback = useMemo(
    () => validatePipelineDepth(formData.depth_m),
    [formData.depth_m]
  );

  const selectedStatus = useMemo(
    () => PIPELINE_STATUSES.find(s => s.value === formData.status),
    [formData.status]
  );

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setCoordError('');
    setDepthError('');
    setWarnings([]);

    // 1. Parse the coordinate JSON
    let parsedCoords = null;
    if (!formData.path_coordinates_text.trim()) {
      setCoordError('Pipeline path coordinates are required.');
      setIsSubmitting(false);
      return;
    }
    try {
      parsedCoords = JSON.parse(formData.path_coordinates_text);
    } catch (err) {
      setCoordError('Invalid JSON format for coordinates.');
      setIsSubmitting(false);
      return;
    }

    // 2. Validate against the shared rules the clearance scan depends on.
    // The old check was `typeof p.lat === 'number'`, which accepts a latitude
    // of 250 and cannot detect a transposed lat/lng pair.
    const pathCheck = validatePipelinePath(parsedCoords);
    if (pathCheck.errors.length > 0) {
      setCoordError(pathCheck.errors.join(' '));
      setIsSubmitting(false);
      return;
    }

    const depthCheck = validatePipelineDepth(formData.depth_m);
    if (depthCheck.errors.length > 0) {
      setDepthError(depthCheck.errors.join(' '));
      setIsSubmitting(false);
      return;
    }

    try {
      const created = await PipelineService.createPipeline({
        name: formData.name,
        description: formData.description,
        material: formData.material,
        diameter_inches: formData.diameter_inches,
        install_date: formData.install_date,
        path_coordinates: pathCheck.coordinates,
        area_code: formData.area_code,
        depth_m: formData.depth_m,
        depth_source: formData.depth_source,
        status: formData.status,
        pressure_zone: formData.pressure_zone,
      });

      const allWarnings = [...pathCheck.warnings, ...(created.warnings || [])];

      toast({
        title: 'Pipeline Created',
        description: allWarnings.length > 0
          ? `Saved with ${allWarnings.length} data-quality note(s).`
          : 'Successfully saved to database.',
        status: allWarnings.length > 0 ? 'warning' : 'success',
        duration: allWarnings.length > 0 ? 8000 : 3000,
        isClosable: true,
      });

      if (allWarnings.length > 0) {
        // Keep the modal open so the notes can actually be read and acted on.
        setWarnings(allWarnings);
        setIsSubmitting(false);
        onSuccess();
        return;
      }

      onSuccess();
      onClose();
    } catch (err) {
      const detail = err.details?.length ? err.details.join(' ') : err.message;
      toast({ title: 'Creation Failed', description: detail, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSuccess, onClose, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="xl">
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent as="form" onSubmit={handleSubmit} bg={modalBg} backdropFilter="blur(16px)" borderRadius="2xl" borderWidth="1px" borderColor={borderColor} shadow="2xl">
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
          <HStack>
            <Flex p={2} bg="purple.500" borderRadius="md"><Icon as={FaRoute} color="white" /></Flex>
            <Text fontWeight="800">Register New Pipeline</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton mt={3} />

        <ModalBody pb={6} pt={6}>
          <VStack spacing={5} align="stretch">

            {warnings.length > 0 && (
              <Alert status="warning" borderRadius="lg" flexDirection="column" alignItems="flex-start" fontSize="sm">
                <HStack><AlertIcon /><Text fontWeight="700">Saved with data-quality notes</Text></HStack>
                <VStack align="start" spacing={1} mt={2} pl={6}>
                  {warnings.map((w, i) => <Text key={i} fontSize="xs">• {w}</Text>)}
                </VStack>
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="700">Pipeline Designation</FormLabel>
              <InputGroup>
                <InputLeftElement><Icon as={FaRoute} color="gray.400" /></InputLeftElement>
                <Input name="name" placeholder="e.g., Main St Line A" value={formData.name} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="700">Description</FormLabel>
              <InputGroup>
                <InputLeftElement><Icon as={FaInfoCircle} color="gray.400" /></InputLeftElement>
                <Input name="description" placeholder="Optional description..." value={formData.description} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
              </InputGroup>
            </FormControl>

            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700">Material</FormLabel>
                <InputGroup>
                  <InputLeftElement><Icon as={FaTools} color="gray.400" /></InputLeftElement>
                  <Input name="material" placeholder="e.g., PVC, Ductile Iron" value={formData.material} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700">Diameter (in)</FormLabel>
                <InputGroup>
                  <InputLeftElement><Icon as={FaRulerCombined} color="gray.400" /></InputLeftElement>
                  <Input type="number" step="0.1" name="diameter_inches" placeholder="e.g., 12.0" value={formData.diameter_inches} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700">Install Date</FormLabel>
                <InputGroup>
                  <InputLeftElement><Icon as={FaCalendarAlt} color="gray.400" /></InputLeftElement>
                  <Input type="date" name="install_date" value={formData.install_date} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700">Area Zone</FormLabel>
                <InputGroup>
                  <InputLeftElement><Icon as={FaMapMarkedAlt} color="gray.400" /></InputLeftElement>
                  <Input name="area_code" placeholder="e.g., SECTOR-7" value={formData.area_code} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
                </InputGroup>
              </FormControl>
            </SimpleGrid>

            {/* ============================================================ */}
            {/* --- UPDATE: EXCAVATION CLEARANCE DATA                        */}
            {/* These fields are read directly by the 4D subsurface scan.    */}
            {/* Without depth, this main raises a conflict at every dig      */}
            {/* depth — safe, but it buries the real conflicts in noise.     */}
            {/* ============================================================ */}
            <Box>
              <Divider mb={4} />
              <HStack mb={3} spacing={2}>
                <Icon as={FaShieldAlt} color="orange.400" />
                <Text fontSize="sm" fontWeight="800">Excavation Clearance Data</Text>
                <Tooltip
                  hasArrow
                  label="These fields feed the 4D subsurface clearance scan used before any excavation."
                >
                  <Badge colorScheme="orange" fontSize="9px">SAFETY</Badge>
                </Tooltip>
              </HStack>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isInvalid={!!depthError}>
                  <FormLabel fontSize="sm" fontWeight="700">Cover Depth (m)</FormLabel>
                  <InputGroup>
                    <InputLeftElement><Icon as={FaArrowDown} color="gray.400" /></InputLeftElement>
                    <Input
                      type="number" step="0.05" min="0" max="30"
                      name="depth_m" placeholder="e.g., 1.20"
                      value={formData.depth_m} onChange={handleInputChange}
                      bg={inputBg} borderRadius="lg"
                    />
                  </InputGroup>
                  {depthError ? (
                    <FormErrorMessage fontWeight="600" fontSize="xs">{depthError}</FormErrorMessage>
                  ) : depthFeedback.warnings.length > 0 ? (
                    <FormHelperText fontSize="xs" color="orange.400">{depthFeedback.warnings[0]}</FormHelperText>
                  ) : (
                    <FormHelperText fontSize="xs" color={helperColor}>Metres of cover from surface to crown.</FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Depth Source</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none"><Icon as={FaClipboardCheck} color="gray.400" /></InputLeftElement>
                    <Select
                      name="depth_source" value={formData.depth_source} onChange={handleInputChange}
                      bg={inputBg} borderRadius="lg" pl={10}
                    >
                      <option value="">Not recorded</option>
                      {DEPTH_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </Select>
                  </InputGroup>
                  <FormHelperText fontSize="xs" color={helperColor}>A field locate and an as-built are not the same confidence.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Status</FormLabel>
                  <Select name="status" value={formData.status} onChange={handleInputChange} bg={inputBg} borderRadius="lg">
                    {PIPELINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                  {selectedStatus && !selectedStatus.scanned && (
                    <FormHelperText fontSize="xs" color="red.400" fontWeight="600">
                      Excluded from excavation clearance scans.
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700">Pressure Zone</FormLabel>
                  <Input name="pressure_zone" placeholder="e.g., ZONE-2" value={formData.pressure_zone} onChange={handleInputChange} bg={inputBg} borderRadius="lg" />
                  <FormHelperText fontSize="xs" color={helperColor}>Used by the cascade simulation.</FormHelperText>
                </FormControl>
              </SimpleGrid>

              {!formData.depth_m && (
                <Box mt={3} p={3} bg={noteBg} borderWidth="1px" borderColor={noteBorder} borderRadius="lg">
                  <Text fontSize="xs">
                    <strong>No depth recorded.</strong> The clearance scan treats unknown depth as
                    in-scope, so this main will be flagged on every excavation regardless of how
                    shallow the dig is. That is the safe default, but a register full of unsurveyed
                    mains turns every scan into a wall of conflicts.
                  </Text>
                </Box>
              )}
            </Box>

            <Divider />

            <FormControl isInvalid={!!coordError} isRequired>
              <FormLabel fontSize="sm" fontWeight="700" display="flex" justifyContent="space-between">
                Path Coordinates (JSON)
                {initialCoordinates && <Badge colorScheme="green">Pre-filled from Map</Badge>}
              </FormLabel>
              <Textarea
                name="path_coordinates_text"
                placeholder='[{"lat": 25.06, "lng": -77.34}, ...]'
                value={formData.path_coordinates_text}
                onChange={handleInputChange}
                rows={4}
                fontFamily="monospace"
                fontSize="xs"
                bg={inputBg}
                borderRadius="lg"
                isReadOnly={!!initialCoordinates}
                _readOnly={{ bg: readOnlyBg, cursor: 'not-allowed' }}
              />
              {coordError ? (
                <FormErrorMessage fontWeight="600">{coordError}</FormErrorMessage>
              ) : (
                <FormHelperText fontSize="xs">Auto-populated if drawn on the map. Contains Lat/Lng array.</FormHelperText>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor} bg={footerBg} borderBottomRadius="2xl">
          <Button variant="ghost" onClick={onClose} mr={3} borderRadius="lg">Cancel</Button>
          <Button type="submit" colorScheme="purple" isLoading={isSubmitting} borderRadius="lg" px={8} shadow="md">
            Save Pipeline
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

CreatePipelineForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  initialCoordinates: PropTypes.array,
};

export default CreatePipelineForm;